import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { AdminMenuComponent } from "./admin-menu/admin-menu.component";
import { PaymentMethodSelectionComponent } from "./admin-menu/payment-method-selection/payment-method-selection.component";
import { CreditoComponent } from "./admin-menu/credito/credito.component";
import { ProductSearchComponent } from "./product-search/product-search.component";
import { ProductListComponent } from "./product-list/product-list.component";
import { CustomerSearchComponent } from "./customer-search/customer-search.component";
import { CustomerInputComponent } from "./customer-input/customer-input.component";
import { DiscountInputComponent } from "./discount-input/discount-input.component";
import { HelpComponent } from "./help/help.component";
import { PdvTablesViewComponent } from "./pdv-tables-view/pdv-tables-view.component";
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { CreateSaleFromPdvDto } from './create-sale-from-pdv-dto';
import { Product } from '../../models/product.model';
import { Customer } from '../../models/customer.model';
import { FormsModule } from "@angular/forms";
import { ProductService } from '../../services/product.service';
import { ActionCommandService, ActionCommand } from '../../services/action-command.service';
import { MenuStateService } from '../../services/menu-state.service';
import { CustomerService } from '../../services/customer.service';
import { Subject, take, takeUntil } from 'rxjs';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { PreVendaImportService } from '../../services/pre-venda-import.service';
import { PreVenda } from '../../models/pre-venda.model';
import { ImportPreVendaPdvComponent } from '../sales/import-pre-venda-pdv/import-pre-venda-pdv.component';

const PDV_DRAFT_STORAGE_KEY = 'pdvDraftV1';
const PDV_NEW_CUSTOMER_STORAGE_KEY = 'pdvNewCustomerV1';

@Component({
  selector: 'app-pdv',
  imports: [
    CommonModule,
    AdminMenuComponent,
    PaymentMethodSelectionComponent,
    CreditoComponent,
    ProductSearchComponent,
    ProductListComponent,
    CustomerSearchComponent,
    CustomerInputComponent,
    DiscountInputComponent,
    HelpComponent,
    PdvTablesViewComponent,
    ImportPreVendaPdvComponent,
    InputTextModule,
    TableModule,
    ButtonModule,
    InputNumberModule,
    FormsModule,
    ToastModule,
    ConfirmDialogModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './pdv.component.html'
})
export class PdvComponent implements OnInit, OnDestroy {
  
  @ViewChild(PaymentMethodSelectionComponent) paymentMethodComponent!: PaymentMethodSelectionComponent;
  @ViewChild(ProductSearchComponent) productSearchComponent!: ProductSearchComponent;
  @ViewChild(CustomerSearchComponent) customerSearchComponent!: CustomerSearchComponent;
  @ViewChild(CreditoComponent) creditoComponent!: CreditoComponent;
  
  menuOpened = '';
  isTablesViewOpen = false;
  private shouldRestoreTablesAfterMenuClose = false;
  private shouldRestoreTablesAfterSearch = false;
  customerSearch: string = '';
  customerDocument: string = '';
  /** Comanda cuja venda está sendo fechada (preenchido ao carregar comanda da mesa); usado ao finalizar pagamento para chamar fechar. */
  comandaIdFromTable: number | null = null;
  clienteSelecionadoCredito: Customer | null = null;

  sale: CreateSaleFromPdvDto = {
    discountTotal: 0,
    exchange: 0,
    grossTotal: 0,
    netTotal: 0,
    items: [],
    customerName: '',
    customerId: null,
    paymentMethod: '', // ou o valor padrão que faça sentido
    employeeId: null,
    shiftId: null
  } as CreateSaleFromPdvDto;


  amount: number = 0;
  search: string = '';

  totalItens = 0;
  valorTotal = 0;
  private codeSearchRequestId = 0;
  private readonly destroy$ = new Subject<void>();
  crediarioRemainingAmount: number | null = null;

  // Navegação entre itens da venda
  selectedSaleItemIndex: number = -1;

  constructor(
    private productService: ProductService,
    private actionCommandService: ActionCommandService,
    private menuStateService: MenuStateService,
    private customerService: CustomerService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private preVendaImportService: PreVendaImportService
  ) {}

  private readonly onWindowFocusPdv = (): void => {
    this.tryConsumePdvNewCustomerFromStorage();
  };

  get pdvStatus(): string {
    return localStorage.getItem('pdvId') ? 'PDV ABERTO' : 'PDV FECHADO';
  }

  private readonly onStoragePdv = (ev: StorageEvent): void => {
    if (ev.key === PDV_NEW_CUSTOMER_STORAGE_KEY || ev.key === PDV_DRAFT_STORAGE_KEY) {
      this.tryConsumePdvNewCustomerFromStorage();
    }
  };

  ngOnInit(): void {
    this.registerCommands();
    this.subscribeToMenuState();
    this.tryLoadImportedPreSale();
    this.tryRestorePdvDraft();
    this.tryConsumePdvNewCustomerFromStorage();
    window.addEventListener('focus', this.onWindowFocusPdv);
    window.addEventListener('storage', this.onStoragePdv);
    this.focusProductSearchInput();
  }

  private tryLoadImportedPreSale(): void {
    const preVenda = this.preVendaImportService.consumePreSaleToImport();
    if (preVenda) this.loadImportedPreSale(preVenda);
  }

  private loadImportedPreSale(preVenda: PreVenda): void {
    this.sale = {
      customerName: preVenda.customerName ?? '',
      customerId: preVenda.customerId ?? null,
      items: (preVenda.items ?? []).map((item) => ({
        productName: item.description ?? '',
        productId: item.productId,
        amount: item.amount ?? 0,
        productPrice: item.price ?? 0,
        total: item.total ?? 0,
        productBarCode: item.barCode ?? '',
        productCode: item.code ?? '',
        costPrice: item.costPrice ?? 0
      })),
      discountTotal: preVenda.discount ?? 0,
      exchange: 0,
      grossTotal: preVenda.grossTotal ?? 0,
      netTotal: preVenda.netTotal ?? 0,
      shiftId: null,
      employeeId: preVenda.employeeId ?? null,
      paymentMethod: ''
    };
    this.customerDocument = preVenda.cpfCnpj ?? '';
    this.messageService.add({
      severity: 'success',
      summary: 'Sucesso!',
      detail: 'Pré-venda importada no PDV.',
      life: 3000
    });
  }

  openImportPreVenda(): void {
    this.openOverlayMenu('IMPORT_PRE_VENDA');
  }

  onPreVendaImported(preVenda: PreVenda): void {
    this.loadImportedPreSale(preVenda);
    this.closeMenu();
  }

  private subscribeToMenuState(): void {
    this.menuStateService.isAdminMenuOpen$.pipe(takeUntil(this.destroy$)).subscribe(isOpen => {
      const wasOpen = this.menuOpened === 'ADMIN_MENU' || this.menuOpened === 'PAYMENT_METHOD';
      if (isOpen) {
        this.hideTablesForOverlay();
      }
      this.menuOpened = isOpen ? 'ADMIN_MENU' : '';
      if (!isOpen) {
        this.restoreTablesIfNeeded();
      }
      // Se o menu admin foi fechado, re-registrar comandos do PDV e focar o input de busca
      if (!isOpen && wasOpen) {
        // Usar setTimeout para garantir que o AdminMenuComponent foi destruído antes de re-registrar
        setTimeout(() => {
          this.registerCommands(); // Re-registrar comandos do PDV quando o menu admin fecha
          this.focusProductSearchInput(); // Focar o input de busca de produtos
        }, 100);
      }
    });

    this.menuStateService.currentSubMenu$.pipe(takeUntil(this.destroy$)).subscribe(subMenu => {
      if (subMenu === 'CREDITO') {
        this.hideTablesForOverlay();
        this.menuOpened = 'CREDITO';
        // Focar no campo de busca de clientes quando o crediário abrir
        setTimeout(() => {
          this.focusCustomerSearchInput();
        }, 100);
      } else if (this.menuOpened === 'CREDITO' && subMenu !== 'CREDITO') {
        // Se o crediário estava aberto e agora fechou, voltar o foco para o input de busca de produtos
        this.menuOpened = '';
        this.restoreTablesIfNeeded();
        setTimeout(() => {
          this.focusProductSearchInput();
        }, 100);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('focus', this.onWindowFocusPdv);
    window.removeEventListener('storage', this.onStoragePdv);
    this.unregisterCommands();
  }

  /**
   * Captura eventos de teclado globalmente no componente
   * Permite navegação com setas mesmo quando o input não está focado
   */
  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    // Permitir ESC para fechar qualquer menu
    if(event.key === 'Escape') {
      // Se o menu de pagamento está aberto, fechar via evento
      if(this.menuOpened === 'PAYMENT_METHOD' && this.paymentMethodComponent) {
        this.paymentMethodComponent.cancel();
      } else if(this.menuOpened === 'ADMIN_MENU') {
        // Se o menu admin está aberto, verificar se há submenu
        // Se houver submenu, voltar para o menu principal; caso contrário, fechar o menu admin
        this.menuStateService.currentSubMenu$.pipe(take(1)).subscribe(subMenu => {
          if (subMenu !== '') {
            this.menuStateService.goBackToMainMenu();
          } else {
            this.menuStateService.closeAdminMenu();
          }
        });
      } else {
        this.closeMenu();
      }
      return;
    }

    // Permitir F1 sempre (mesmo com menus abertos) para abrir/fechar ajuda
    if(event.key === 'F1' || event.code === 'F1') {
      event.preventDefault();
      this.openHelp();
      return;
    }

    // Permitir F8 sempre para abrir/fechar visualização de mesas
    if(event.key === 'F8') {
      event.preventDefault();
      this.openTablesView();
      return;
    }

    // Ignorar se algum menu estiver aberto (exceto teclas especiais e navegação para menus que gerenciam teclado)
    // Permitir que componentes de menu gerenciem suas próprias teclas (setas, Enter, etc)
    if(this.menuOpened !== '' && event.key !== 'Escape' && event.key !== 'F1' && event.key !== 'F8') {
      // Se o menu de lista de produtos está aberto, deixar ele gerenciar as teclas
      if(this.menuOpened === 'PRODUCT_LIST') {
        // O ProductListComponent gerencia suas próprias teclas via HostListener
        return;
      }
      // Se o menu de ajuda está aberto, permitir apenas ESC para fechar
      if(this.menuOpened === 'HELP') {
        return;
      }
      // Para outros menus, também permitir que gerenciem suas teclas
      return;
    }

    // Verificar se está em input ou textarea
    const target = event.target as HTMLElement;
    const isInputFocused = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA');
    
    // Se há itens na venda, permitir navegação e ações
    const canNavigateItems = this.sale.items.length > 0;

    // Processar tecla + para incrementar quantidade do item selecionado
    // Suporta: tecla + normal, Shift+= (no layout padrão), e NumpadAdd (teclado numérico)
    if(event.key === "+" || event.key === "NumpadAdd" || (event.key === "=" && event.shiftKey)){
      if(canNavigateItems && this.selectedSaleItemIndex >= 0){
        event.preventDefault();
        this.handleIncrementQuantity();
      }
      return;
    }
    
    // Processar tecla - para decrementar quantidade do item selecionado
    // Suporta: tecla - normal e NumpadSubtract (teclado numérico)
    if(event.key === "-" || event.key === "_" || event.key === "NumpadSubtract"){
      if(canNavigateItems && this.selectedSaleItemIndex >= 0){
        event.preventDefault();
        this.handleDecrementQuantity();
      }
      return;
    }
    
    // Processar tecla Delete para remover o item selecionado
    if(event.key === "Delete" || event.key === "Del"){
      if(canNavigateItems && this.selectedSaleItemIndex >= 0){
        event.preventDefault();
        this.handleDeleteSelectedItem();
      }
      return;
    }

    // Processar navegação com setas
    if(event.key === "ArrowDown" || event.key === "ArrowUp") {
      // Verificar se há resultados de busca no componente product-search
      const hasSearchResults = this.productSearchComponent && this.productSearchComponent.searchResults.length > 0;
      
      // Se o input está focado E há resultados, deixar o product-search gerenciar
      if(isInputFocused && hasSearchResults) {
        // Não fazer nada aqui, deixar o product-search gerenciar
        return;
      }
      
      // Caso contrário, se há itens na venda, navegar neles
      if(canNavigateItems) {
        // Permitir navegação quando:
        // 1. Não está digitando, OU
        // 2. Está digitando mas não há resultados de busca
        if(!isInputFocused || !hasSearchResults) {
          event.preventDefault();
          if(event.key === "ArrowDown") {
            this.navigateDown();
          } else if(event.key === "ArrowUp") {
            this.navigateUp();
          }
        }
      }
    }
  }

  private registerCommands(): void {
    // Registrar comando F1 para abrir ajuda
    this.actionCommandService.registerCommand({
      getKey: () => 'F1',
      execute: () => this.openHelp(),
      getDescription: () => 'Ajuda'
    });

    // Registrar comando F2 para buscar cliente
    this.actionCommandService.registerCommand({
      getKey: () => 'F2',
      execute: () => this.openCustomerSearch(),
      getDescription: () => 'Buscar Cliente'
    });

    // Registrar comando F4 para abrir menu admin
    this.actionCommandService.registerCommand({
      getKey: () => 'F4',
      execute: () => this.openAdminMenu(),
      getDescription: () => 'Menu Admin'
    });

    // Registrar comando F5 para fechar venda
    this.actionCommandService.registerCommand({
      getKey: () => 'F5',
      execute: () => this.openPaymentMethod(),
      getDescription: () => 'Fechar Venda'
    });

    // Registrar comando F6 para cancelar venda
    this.actionCommandService.registerCommand({
      getKey: () => 'F6',
      execute: () => this.cancelSale(),
      getDescription: () => 'Cancelar Venda'
    });

    // Registrar comando F7 para entrada manual de cliente
    this.actionCommandService.registerCommand({
      getKey: () => 'F7',
      execute: () => this.openCustomerInput(),
      getDescription: () => 'CPF/CNPJ Manual'
    });

    // Registrar comando F3 para aplicar desconto
    this.actionCommandService.registerCommand({
      getKey: () => 'F3',
      execute: () => this.openDiscountInput(),
      getDescription: () => 'Aplicar Desconto'
    });

    // Registrar comando F8 para visualização de mesas
    this.actionCommandService.registerCommand({
      getKey: () => 'F8',
      execute: () => this.openTablesView(),
      getDescription: () => 'Mesas'
    });

    // Registrar comando F9 para importar pré-venda
    this.actionCommandService.registerCommand({
      getKey: () => 'F9',
      execute: () => this.openImportPreVenda(),
      getDescription: () => 'Importar Pré-Venda'
    });

    // Registrar comando F11 para listar produtos
    this.actionCommandService.registerCommand({
      getKey: () => 'F11',
      execute: () => this.openProductList(),
      getDescription: () => 'Listar Produtos'
    });

    // Registrar comando ESC para fechar menu
    this.actionCommandService.registerCommand({
      getKey: () => 'ESC',
      execute: () => this.closeMenu(),
      getDescription: () => 'Fechar Menu'
    });
  }

  private unregisterCommands(): void {
    this.actionCommandService.unregisterCommand('F1');
    this.actionCommandService.unregisterCommand('F2');
    this.actionCommandService.unregisterCommand('F3');
    this.actionCommandService.unregisterCommand('F4');
    this.actionCommandService.unregisterCommand('F5');
    this.actionCommandService.unregisterCommand('F6');
    this.actionCommandService.unregisterCommand('F7');
    this.actionCommandService.unregisterCommand('F8');
    this.actionCommandService.unregisterCommand('F9');
    this.actionCommandService.unregisterCommand('F11');
    this.actionCommandService.unregisterCommand('ESC');
  }

  openAdminMenu(): void {
    this.hideTablesForOverlay();
    this.menuStateService.openAdminMenu();
  }

  openTablesView(): void {
    if (this.isTablesViewOpen) {
      this.isTablesViewOpen = false;
      this.shouldRestoreTablesAfterMenuClose = false;
      this.shouldRestoreTablesAfterSearch = false;
      if (this.menuOpened === '') {
        this.focusProductSearchInput();
      }
    } else {
      this.isTablesViewOpen = true;
      this.shouldRestoreTablesAfterMenuClose = false;
      if (this.menuOpened !== '') {
        this.menuOpened = '';
        this.customerSearch = '';
        this.registerCommands();
      }
    }
  }

  openPaymentMethod(): void {
    this.crediarioRemainingAmount = null;
    this.openOverlayMenu('PAYMENT_METHOD');
    
    // Passar dados da venda para o componente de seleção de pagamento
    setTimeout(() => {
      if (this.paymentMethodComponent) {
        this.paymentMethodComponent.setSaleData(this.sale, this.sale.netTotal, this.comandaIdFromTable);
      }
    }, 0);
  }

  closeMenu(): void {
    this.menuOpened = '';
    this.restoreTablesIfNeeded();
    this.customerSearch = '';
    // Re-registrar comandos do PDV quando um menu é fechado
    // Isso garante que os comandos não sejam perdidos após componentes filhos serem destruídos
    this.registerCommands();
    this.focusProductSearchInput();
  }

  private hideTablesForOverlay(): void {
    if (this.isTablesViewOpen) {
      this.isTablesViewOpen = false;
      this.shouldRestoreTablesAfterMenuClose = true;
    }
  }

  private restoreTablesIfNeeded(): void {
    if (this.shouldRestoreTablesAfterMenuClose && !this.isSearchingProducts()) {
      this.isTablesViewOpen = true;
      this.shouldRestoreTablesAfterMenuClose = false;
    }
  }

  private isSearchingProducts(): boolean {
    return this.search.trim().length > 0;
  }

  private handleTablesVisibilityForProductSearch(): void {
    if (this.isSearchingProducts()) {
      if (this.isTablesViewOpen) {
        this.isTablesViewOpen = false;
        this.shouldRestoreTablesAfterSearch = true;
      }
      return;
    }

    if (this.shouldRestoreTablesAfterSearch && this.menuOpened === '') {
      this.isTablesViewOpen = true;
      this.shouldRestoreTablesAfterSearch = false;
    }
  }

  private openOverlayMenu(menu: string, keepTablesVisible = false): void {
    if (!keepTablesVisible) {
      this.hideTablesForOverlay();
    }
    this.menuOpened = menu;
  }

  /**
   * Foca o input de busca de produtos
   */
  private focusProductSearchInput(): void {
    // Usar setTimeout para garantir que o DOM foi atualizado
    setTimeout(() => {
      const searchInput = document.getElementById('search');
      if (searchInput) {
        // Verificar se o input está visível antes de focar
        const isVisible = searchInput.offsetParent !== null;
        if (isVisible) {
          searchInput.focus();
        } else {
          // Se não estiver visível, tentar novamente após um pequeno delay
          setTimeout(() => {
            const retryInput = document.getElementById('search');
            if (retryInput && retryInput.offsetParent !== null) {
              retryInput.focus();
            }
          }, 50);
        }
      }
    }, 0);
  }

  /**
   * Foca o input de busca de clientes no crediário
   */
  private focusCustomerSearchInput(): void {
    const tryFocus = (attempt: number = 0): void => {
      let el = document.getElementById('customer-search-credito') as HTMLInputElement | null;
      if (el && el.tagName !== 'INPUT') {
        el = el.querySelector('input') as HTMLInputElement | null;
      }
      if (el && el.offsetParent !== null) {
        el.focus();
        el.select();
        return;
      }
      if (attempt < 6) {
        setTimeout(() => tryFocus(attempt + 1), 80 * (attempt + 1));
      }
    };
    setTimeout(() => tryFocus(0), 0);
  }

  /**
   * Foca o input de busca de clientes no PDV (F2)
   */
  private focusCustomerSearchInputForPdv(): void {
    // Tentar múltiplas vezes com delays crescentes para garantir que o elemento foi renderizado
    const tryFocus = (attempt: number = 0) => {
      // Primeiro tentar pelo ID direto
      let customerSearchInput = document.getElementById('customer-search') as HTMLInputElement;
      
      // Se não encontrar pelo ID, tentar encontrar o input dentro do PrimeNG pInputText
      if (!customerSearchInput) {
        const pInputTextContainer = document.querySelector('[id="customer-search"]')?.closest('.p-inputtext');
        if (pInputTextContainer) {
          customerSearchInput = pInputTextContainer.querySelector('input') as HTMLInputElement;
        }
      }
      
      // Se ainda não encontrou, tentar buscar por qualquer input dentro do container
      if (!customerSearchInput) {
        const container = document.querySelector('[id="customer-search"]')?.parentElement;
        if (container) {
          customerSearchInput = container.querySelector('input') as HTMLInputElement;
        }
      }
      
      if (customerSearchInput) {
        // Verificar se o input está visível antes de focar
        const isVisible = customerSearchInput.offsetParent !== null;
        if (isVisible) {
          customerSearchInput.focus();
          customerSearchInput.select();
          return true;
        }
      }
      
      // Se não conseguiu focar e ainda há tentativas, tentar novamente
      if (attempt < 5) {
        setTimeout(() => {
          tryFocus(attempt + 1);
        }, 100 * (attempt + 1)); // Delay crescente: 100ms, 200ms, 300ms, etc.
      }
      return false;
    };
    
    // Começar a tentar após um pequeno delay inicial para garantir que o *ngIf renderizou
    setTimeout(() => {
      tryFocus(0);
    }, 100);
  }

  openCustomerSearch(): void {
    // Se já estiver aberto, fechar. Caso contrário, abrir
    if(this.menuOpened === 'CUSTOMER_SEARCH') {
      this.closeMenu();
    } else {
      this.openOverlayMenu('CUSTOMER_SEARCH');
      // Focar no input de busca de cliente após o DOM ser atualizado
      // Usar setTimeout para garantir que o Angular tenha renderizado o *ngIf
      setTimeout(() => {
        this.focusCustomerSearchInputForPdv();
      }, 0);
    }
  }

  openCustomerInput(): void {
    // Se já estiver aberto, fechar. Caso contrário, abrir
    if(this.menuOpened === 'CUSTOMER_INPUT') {
      this.closeMenu();
    } else {
      this.openOverlayMenu('CUSTOMER_INPUT');
    }
  }

  openProductList(): void {
    // Se já estiver aberto, fechar. Caso contrário, abrir
    if(this.menuOpened === 'PRODUCT_LIST') {
      this.closeMenu();
    } else {
      this.openOverlayMenu('PRODUCT_LIST', true);
    }
  }

  openDiscountInput(): void {
    // Se já estiver aberto, fechar. Caso contrário, abrir
    if(this.menuOpened === 'DISCOUNT_INPUT') {
      this.closeMenu();
    } else {
      this.openOverlayMenu('DISCOUNT_INPUT');
    }
  }

  openHelp(): void {
    // Se já estiver aberto, fechar. Caso contrário, abrir
    if(this.menuOpened === 'HELP') {
      this.closeMenu();
    } else {
      this.openOverlayMenu('HELP');
    }
  }

  onCloseComandaRequest(event: { sale: CreateSaleFromPdvDto; comandaId: number }): void {
    // Atualiza a venda com os itens da comanda (cópia explícita para o binding atualizar)
    const sale = event.sale;
    this.sale = {
      customerName: sale.customerName ?? '',
      customerId: sale.customerId ?? null,
      items: Array.isArray(sale.items) ? [...sale.items] : [],
      discountTotal: sale.discountTotal ?? 0,
      exchange: sale.exchange ?? 0,
      grossTotal: sale.grossTotal ?? 0,
      netTotal: sale.netTotal ?? 0,
      paymentMethod: '',
      employeeId: null,
      shiftId: sale.shiftId ?? null,
    } as CreateSaleFromPdvDto;
    this.comandaIdFromTable = event.comandaId;
    // Não fecha o menu de mesas: usuário pode continuar vendo as mesas e finalizar a venda (F5)
  }

  onTablesViewCloseRequest(): void {
    this.closeMenu();
  }

  onDiscountConfirmed(discountAmount: number): void {
    this.sale.discountTotal = discountAmount;
    this.sale.netTotal = this.sale.grossTotal - this.sale.discountTotal;
    this.closeMenu();
  }

  onDiscountCancelled(): void {
    this.closeMenu();
  }

  onCrediarioRequested(event: { payments: Array<{ method: string; amount: number }>; remainingAmount: number }): void {
    const totals = this.calculatePaymentTotals(event.payments);
    this.sale.moneyTotal = totals['MONEY'];
    this.sale.pixTotal = totals['PIX'];
    this.sale.creditCardTotal = totals['CREDIT_CARD'];
    this.sale.debitCardTotal = totals['DEBIT_CARD'];
    this.sale.checkTotal = totals['CHECK'];
    this.sale.creditTotal = totals['CREDIT'];
    this.sale.bankTransferTotal = totals['BANK_TRANSFER'];
    this.sale.voucherTotal = totals['VOUCHER'];
    this.crediarioRemainingAmount = event.remainingAmount > 0 ? event.remainingAmount : this.sale.netTotal;
    this.menuStateService.openCreditoMenu();
  }

  onCustomerSearchKeyup(event: any): void {
    if(this.customerSearchComponent) {
      this.customerSearchComponent.onSearchKeyup(event);
    }
  }

  onCustomerSearchInput(): void {
    if(this.customerSearchComponent) {
      this.customerSearchComponent.onSearchInputChange(this.customerSearch);
    }
  }

  onCustomerSelected(customer: Customer): void {
    this.sale.customerId = customer.id;
    this.sale.customerName = customer.fullName;
    this.customerDocument = customer.cpf || '';
    this.closeMenu();
  }

  onCustomerDataChange(data: {name: string, document: string}): void {
    this.sale.customerName = data.name;
    this.customerDocument = data.document || '';
    this.sale.customerId = null; // Cliente manual não tem ID
  }

  onCustomerConfirmed(data: {name: string, document: string}): void {
    this.sale.customerName = data.name;
    this.customerDocument = data.document || '';
    this.sale.customerId = null; // Cliente manual não tem ID
    this.closeMenu();
  }

  onClienteRemovidoCredito(): void {
    this.clienteSelecionadoCredito = null;
    this.sale.customerId = null;
    this.sale.customerName = '';
    this.customerDocument = '';
    this.customerSearch = '';
    this.customerSearchComponent?.resetSearch();
    if (this.creditoComponent) {
      this.creditoComponent.setClienteSelecionado(null);
    }
    setTimeout(() => this.focusCustomerSearchInput(), 0);
  }

  onClienteSelecionadoCredito(customer: Customer): void {
    this.clienteSelecionadoCredito = customer;
    // Atualizar também no sale para garantir que está sincronizado
    this.sale.customerId = customer.id;
    this.sale.customerName = customer.fullName;
    this.customerDocument = customer.cpf || '';

    this.customerSearch = '';
    this.customerSearchComponent?.resetSearch();

    if (this.creditoComponent) {
      this.creditoComponent.setClienteSelecionado(customer);
      setTimeout(() => {
        this.creditoComponent.focusNumeroParcelas();
      }, 100);
    }
  }

  openCustomerFormFromPdv(): void {
    try {
      const draft = {
        sale: this.sale,
        customerSearch: this.customerSearch,
        comandaIdFromTable: this.comandaIdFromTable,
        customerDocument: this.customerDocument,
        selectedSaleItemIndex: this.selectedSaleItemIndex,
        clienteSelecionadoCredito: this.clienteSelecionadoCredito,
        menuOpened: this.menuOpened
      };
      localStorage.setItem(PDV_DRAFT_STORAGE_KEY, JSON.stringify(draft));
    } catch {
      /* ignore */
    }
    const url = `${window.location.origin}/app/customers/new?fromPdv=1`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private tryRestorePdvDraft(): void {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(PDV_DRAFT_STORAGE_KEY);
    } catch {
      return;
    }
    if (!raw) {
      return;
    }
    try {
      const d = JSON.parse(raw) as {
        sale: CreateSaleFromPdvDto;
        customerSearch?: string;
        comandaIdFromTable?: number | null;
        customerDocument?: string;
        selectedSaleItemIndex?: number;
        clienteSelecionadoCredito?: Customer | null;
        menuOpened?: string;
      };
      if (d.sale) {
        this.sale = { ...d.sale, items: Array.isArray(d.sale.items) ? [...d.sale.items] : [] };
      }
      this.customerSearch = d.customerSearch ?? '';
      this.comandaIdFromTable = d.comandaIdFromTable ?? null;
      this.customerDocument = d.customerDocument ?? '';
      this.selectedSaleItemIndex = d.selectedSaleItemIndex ?? -1;
      this.clienteSelecionadoCredito = d.clienteSelecionadoCredito ?? null;
      if (d.menuOpened === 'CREDITO') {
        this.menuStateService.openCreditoMenu();
      }
    } catch {
      localStorage.removeItem(PDV_DRAFT_STORAGE_KEY);
    }
  }

  private tryConsumePdvNewCustomerFromStorage(): void {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(PDV_NEW_CUSTOMER_STORAGE_KEY);
    } catch {
      return;
    }
    if (!raw) {
      return;
    }
    try {
      const c = JSON.parse(raw) as Customer;
      localStorage.removeItem(PDV_NEW_CUSTOMER_STORAGE_KEY);
      localStorage.removeItem(PDV_DRAFT_STORAGE_KEY);
      if (c?.id) {
        this.onClienteSelecionadoCredito(c);
      }
    } catch {
      localStorage.removeItem(PDV_NEW_CUSTOMER_STORAGE_KEY);
    }
  }

  abrirCadastroClienteCredito(): void {
    if (this.creditoComponent) {
      this.creditoComponent.openCadastroCliente();
    }
  }

  clearPdv(): void {
    // Limpar todos os dados da venda
    this.sale = {
      customerName: '',
      customerId: null,
      items: [],
      discountTotal: 0,
      exchange: 0,
      grossTotal: 0,
      netTotal: 0,
      paymentMethod: '',
      employeeId: null,
      shiftId: null
    } as CreateSaleFromPdvDto;
    
    this.customerDocument = '';
    this.search = '';
    this.amount = 1;
    this.selectedSaleItemIndex = -1;
    this.comandaIdFromTable = null;
    this.clienteSelecionadoCredito = null;
    this.crediarioRemainingAmount = null;

    try {
      localStorage.removeItem(PDV_DRAFT_STORAGE_KEY);
    } catch {
      /* ignore */
    }

    // Garantir que o menu está fechado e focar no input
    this.closeMenu();
  }

  private calculatePaymentTotals(payments: Array<{ method: string; amount: number }>): Record<string, number> {
    const totals: Record<string, number> = {
      MONEY: 0,
      PIX: 0,
      CREDIT_CARD: 0,
      DEBIT_CARD: 0,
      CHECK: 0,
      CREDIT: 0,
      BANK_TRANSFER: 0,
      VOUCHER: 0
    };

    for (const payment of payments || []) {
      if (!payment || !Number.isFinite(payment.amount) || payment.amount <= 0) {
        continue;
      }
      if (totals[payment.method] !== undefined) {
        totals[payment.method] = Math.round((totals[payment.method] + payment.amount) * 100) / 100;
      }
    }

    return totals;
  }

  cancelSale(): void {
    this.confirmationService.confirm({
      message: 'Tem certeza que deseja cancelar a venda em andamento? Todos os itens serão removidos.',
      header: 'Cancelar Venda',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      defaultFocus: 'accept', // Tentar focar no botão de aceitar
      acceptButtonStyleClass: 'p-button-danger', // Estilizar o botão de aceitar
      rejectButtonStyleClass: 'p-button-secondary', // Estilizar o botão de rejeitar
      accept: () => {
        this.clearPdv();
        this.closeMenu();
        // Restaurar foco e garantir que os comandos estão registrados
        setTimeout(() => {
          this.registerCommands(); // Re-registrar comandos para garantir que estão ativos
          this.focusProductSearchInput();
        }, 150);
      },
      reject: () => {
        // Restaurar foco e garantir que os comandos estão registrados após rejeitar
        setTimeout(() => {
          this.registerCommands(); // Re-registrar comandos para garantir que estão ativos
          this.focusProductSearchInput();
        }, 150);
      }
    });
    
    // Workaround para o bug do PrimeNG: focar manualmente no botão de aceitar após o diálogo abrir
    // Fazer múltiplas tentativas para garantir que o botão seja encontrado
    const tryFocusAccept = (attempt: number = 0) => {
      const acceptButton = document.querySelector('.p-confirm-dialog-accept') as HTMLButtonElement;
      if (acceptButton) {
        acceptButton.focus();
        // Garantir que o botão está realmente focado
        setTimeout(() => {
          if (document.activeElement !== acceptButton) {
            acceptButton.focus();
          }
        }, 50);
      } else if (attempt < 5) {
        // Tentar novamente se o botão ainda não foi encontrado
        setTimeout(() => {
          tryFocusAccept(attempt + 1);
        }, 100 * (attempt + 1));
      }
    };
    
    setTimeout(() => {
      tryFocusAccept(0);
    }, 150);
  }

  onProductSelected(event: {product: Product, amount: number}): void {
    this.amount = event.amount;
    this.addItem(event.product);
    this.selectedSaleItemIndex = -1;
    this.search = '';
    this.handleTablesVisibilityForProductSearch();
    // Se o menu de lista de produtos estiver aberto, fechar após selecionar
    if(this.menuOpened === 'PRODUCT_LIST') {
      this.closeMenu();
    }
  }

  onCodeSearchRequested(event: {searchTerm: string, amount: number}): void {
    const requestId = ++this.codeSearchRequestId;
    const submittedSearch = this.search;
    this.amount = event.amount;
    this.productService.getByCodeOrBarCode(event.searchTerm, true).subscribe({
      next: (response) => {
        if (requestId !== this.codeSearchRequestId) {
          return;
        }
        if(response.data != null){
          this.addItem(response.data);
          return;
        }

        // Limpa somente se o usuário não iniciou uma nova busca.
        if (this.search === submittedSearch) {
          this.search = '';
          this.handleTablesVisibilityForProductSearch();
        }
      },
      error: () => {
        if (requestId !== this.codeSearchRequestId) {
          return;
        }

        if (this.search === submittedSearch) {
          this.handleTablesVisibilityForProductSearch();
        }
      }
    });
  }

  onSearchKeyup(event: any): void {
    if(this.productSearchComponent) {
      this.productSearchComponent.onSearchKeyup(event);
    }
  }

  onSearchInput(): void {
    this.codeSearchRequestId++;
    this.handleTablesVisibilityForProductSearch();
    if(this.productSearchComponent) {
      this.productSearchComponent.onSearchInputChange(this.search);
    }
  }


  /**
   * Navega para baixo nos itens da venda
   */
  private navigateDown(): void {
    if(this.sale.items.length > 0){
      this.selectedSaleItemIndex = Math.min(this.selectedSaleItemIndex + 1, this.sale.items.length - 1);
      // Scroll automático para o item selecionado
      this.scrollToSelectedItem();
    }
  }

  /**
   * Navega para cima nos itens da venda
   */
  private navigateUp(): void {
    if(this.sale.items.length > 0){
      this.selectedSaleItemIndex = Math.max(this.selectedSaleItemIndex - 1, -1);
      // Scroll automático para o item selecionado
      this.scrollToSelectedItem();
    }
  }

  /**
   * Faz scroll automático para o item selecionado na lista de venda
   */
  private scrollToSelectedItem(): void {
    if(this.selectedSaleItemIndex >= 0 && this.selectedSaleItemIndex < this.sale.items.length) {
      // Usar setTimeout para garantir que o DOM foi atualizado
      setTimeout(() => {
        const element = document.getElementById(`sale-item-${this.selectedSaleItemIndex}`);
        const container = document.getElementById('itensTable');
        
        if(element && container) {
          const elementRect = element.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          
          // Verificar se o elemento está visível no container
          const isVisible = (
            elementRect.top >= containerRect.top &&
            elementRect.bottom <= containerRect.bottom
          );
          
          // Se não estiver visível, fazer scroll
          if(!isVisible) {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      }, 0);
    }
  }

  addItem(product: Product){
    if(localStorage.getItem('pdvId') == null){
      this.messageService.add({ 
        severity: 'warn', 
        summary: 'Atenção!', 
        detail: 'PDV fechado. Por favor, abra um turno antes de adicionar produtos.', 
        life: 4000 
      });
      this.search = '';
      this.selectedSaleItemIndex = -1;
      return;
    }
    // this.sale.totalAmount += this.amount;
    this.sale.grossTotal += this.amount * product.priceSelling;
    this.sale.netTotal = this.sale.grossTotal - this.sale.discountTotal;

    this.sale.items.unshift({
      productId: product.id,
      productName: product.description,
      amount: this.amount,
      productPrice: product.priceSelling,
      total: this.amount * product.priceSelling,
      productBarCode: product.barCode,
      productCode: product.code,
      costPrice: product.priceCost,
      listPriceSelling: product.listPriceSelling ?? null,
      promotionId: product.promotionId ?? null,
      promotionDescription: product.promotionDescription ?? null
    });

    this.search = '';
    this.amount = 1;
    this.selectedSaleItemIndex = -1;
  }

  deleteItem(index: number): void {
    if (index < 0 || index >= this.sale.items.length) {
      return;
    }
    const line = this.sale.items[index];
    this.sale.grossTotal -= line.total;
    this.sale.netTotal = this.sale.grossTotal - this.sale.discountTotal;
    this.sale.items.splice(index, 1);

    if (this.sale.items.length === 0) {
      this.selectedSaleItemIndex = -1;
    } else if (this.selectedSaleItemIndex === index) {
      this.selectedSaleItemIndex = Math.min(index, this.sale.items.length - 1);
    } else if (this.selectedSaleItemIndex > index) {
      this.selectedSaleItemIndex -= 1;
    }
    if (this.selectedSaleItemIndex >= this.sale.items.length) {
      this.selectedSaleItemIndex = Math.max(this.sale.items.length - 1, -1);
    }
  }

  addQuantity(index: number): void {
    if (index < 0 || index >= this.sale.items.length) {
      return;
    }
    const line = this.sale.items[index];
    this.sale.grossTotal += line.productPrice;
    this.sale.netTotal = this.sale.grossTotal - this.sale.discountTotal;
    const newAmount = line.amount + 1;
    this.sale.items[index] = {
      ...line,
      amount: newAmount,
      total: newAmount * line.productPrice,
    };
  }

  subtractQuantity(index: number): void {
    if (index < 0 || index >= this.sale.items.length) {
      return;
    }
    const line = this.sale.items[index];
    if (line.amount <= 1) {
      return;
    }
    this.sale.grossTotal -= line.productPrice;
    this.sale.netTotal = this.sale.grossTotal - this.sale.discountTotal;
    const newAmount = line.amount - 1;
    this.sale.items[index] = {
      ...line,
      amount: newAmount,
      total: newAmount * line.productPrice,
    };
  }

  /**
   * Incrementa a quantidade do item selecionado na lista de venda
   */
  private handleIncrementQuantity(): void {
    // Só processar se houver um item selecionado na lista de venda
    if(this.selectedSaleItemIndex >= 0 && 
       this.selectedSaleItemIndex < this.sale.items.length) {
      this.addQuantity(this.selectedSaleItemIndex);
    }
  }

  /**
   * Decrementa a quantidade do item selecionado na lista de venda
   */
  private handleDecrementQuantity(): void {
    // Só processar se houver um item selecionado na lista de venda
    if(this.selectedSaleItemIndex >= 0 && 
       this.selectedSaleItemIndex < this.sale.items.length) {
      this.subtractQuantity(this.selectedSaleItemIndex);
    }
  }

  /**
   * Remove o item selecionado da lista de venda
   */
  private handleDeleteSelectedItem(): void {
    // Só processar se houver um item selecionado na lista de venda
    if(this.selectedSaleItemIndex >= 0 && 
       this.selectedSaleItemIndex < this.sale.items.length) {
      const selectedItem = this.sale.items[this.selectedSaleItemIndex];
      
      // Remover item pelo índice (garantindo que apenas o item selecionado seja removido)
      this.sale.grossTotal -= selectedItem.total;
      this.sale.netTotal = this.sale.grossTotal - this.sale.discountTotal;
      this.sale.items.splice(this.selectedSaleItemIndex, 1);
      
      // Ajustar índice selecionado após remover item
      if(this.selectedSaleItemIndex >= this.sale.items.length){
        this.selectedSaleItemIndex = Math.max(this.sale.items.length - 1, -1);
      }
      // Se a lista ficar vazia, resetar o índice
      if(this.sale.items.length === 0) {
        this.selectedSaleItemIndex = -1;
      }
    }
  }
}
